import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Compass, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { compassApi } from "@/lib/compassApi";
import { projectsApi } from "@/lib/api";
import { useNavigate } from "react-router-dom";

/**
 * Simplification v3 — one-click Evaluate.
 *
 * Normal flow now looks like:
 *   Organization → Evaluate Organization → pick Project → Evaluate.
 *
 * Compass finds (or auto-creates) the reusable Project-level framework
 * internally, hydrates canonical evidence, creates the evaluation, and
 * runs the LLM assessment.  No framework picker, no Draft/Active
 * decision, no scoring configuration — those live under Advanced in
 * the project workspace.
 */
export default function SendToCompassButton({ targetType, targetId,
                                                label = "Evaluate",
                                                variant = "outline", size = "sm",
                                                initialProjectId = null,
                                                testid = "send-to-compass-btn",
                                                onCreated }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initialProjectId || "");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    (async () => {
      const r = await projectsApi.list();
      setProjects(r.data || []);
    })();
  }, [open]);

  const evaluate = async () => {
    if (!projectId) return toast.error("Pick a project");
    setBusy(true);
    try {
      const r = (await compassApi.evaluate({
        project_id: projectId,
        target_type: targetType,
        target_id: targetId,
      })).data;
      if (r.framework_auto_created) {
        toast.success("Compass built a reusable framework for this project");
      } else {
        toast.success("Compass evaluation created");
      }
      setOpen(false);
      onCreated?.(r.evaluation);
      // Land the user directly on the evaluation with results already
      // populated — no configuration screen in between.
      const entityId = projects.find((p) => p.id === projectId)?.entity_id || "";
      nav(`/entity/${entityId}?tab=projects&open_evaluation=${r.evaluation.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluate failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)} data-testid={testid}>
        <Compass className="w-3.5 h-3.5" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !v && !busy && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="send-to-compass-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Compass className="w-4 h-4" /> Evaluate against a Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="send-to-compass-project">
                  <SelectValue placeholder="Pick a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
              <p className="text-[11px] text-white/60 leading-relaxed">
                Compass will use the project's evaluation framework — or build a reusable one from the project's purpose — and give you the assessment directly.
                <span className="block text-white/40 mt-1">
                  Frameworks and scoring settings live in the project workspace under Advanced.
                </span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={evaluate} disabled={busy || !projectId}
                    className="bg-white text-black hover:bg-white/90"
                    data-testid="send-to-compass-start">
              {busy ? "Evaluating…" : "Evaluate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
